import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ClipboardCheck,
  FileText,
  Clock,
  Zap,
  Target,
  Info,
  ChevronRight,
  Loader2,
  Upload,
  CheckCircle2,
} from "lucide-react";
import { JobWizardTabs } from "@/components/JobWizard/JobWizardTabs";
import { SavedJobDescription } from "@/services/jobLibraryService";
import { toast } from "sonner";
import { useJobIntelCredits } from "@/hooks/useJobIntelCredits";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { ReportVersionSelector, type ReportVersion } from "@/components/ReportVersionSelector";
import { QualificationMatchResults } from "@/components/QualificationMatchResults";
import InsufficientCreditsModal from "@/components/InsufficientCreditsModal";
import { findOrCreateSavedJob, linkReportToSavedJob } from "@/services/jobintelSavedJobsService";
import { ResumeLibraryService, type SavedResume } from "@/services/resumeLibraryService";
import { convertPDFToText } from "@/services/pdfConversionService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

type Step = 1 | 2 | 3;
type ResumeMethod = "saved" | "upload" | "paste";

const StepBadge = ({ step, current, label }: { step: Step; current: Step; label: string }) => {
  const isDone = current > step;
  const isActive = current === step;
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
          isDone
            ? "bg-amber-500 border-amber-500 text-white"
            : isActive
            ? "bg-white border-amber-500 text-amber-700"
            : "bg-white border-muted text-muted-foreground"
        }`}
      >
        {isDone ? <CheckCircle2 className="w-4 h-4" /> : step}
      </div>
      <span
        className={`text-sm font-medium ${
          isActive ? "text-amber-700" : isDone ? "text-foreground" : "text-muted-foreground"
        }`}
      >
        {label}
      </span>
    </div>
  );
};

const JobQualificationCheck = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkAndDeductCredits, refundCredits, access } = useJobIntelCredits();

  const [step, setStep] = useState<Step>(1);
  const [reportVersion, setReportVersion] = useState<ReportVersion>("snapshot");

  // Step 1: job description
  const [savedJobData, setSavedJobData] = useState<SavedJobDescription | null>(null);

  // Step 2: resume
  const [resumeMethod, setResumeMethod] = useState<ResumeMethod>("saved");
  const [savedResumes, setSavedResumes] = useState<SavedResume[]>([]);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [selectedResumeId, setSelectedResumeId] = useState<string>("");
  const [pastedResumeText, setPastedResumeText] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [uploadedResumeText, setUploadedResumeText] = useState<string>("");
  const [processingFile, setProcessingFile] = useState(false);

  // Analysis
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [savedJobId, setSavedJobId] = useState<string | undefined>();
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [creditCost, setCreditCost] = useState(3);

  useEffect(() => {
    if (step === 2 && resumeMethod === "saved" && user) {
      loadSavedResumes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, resumeMethod, user]);

  const loadSavedResumes = async () => {
    setLoadingResumes(true);
    try {
      const resumes = await ResumeLibraryService.getSavedResumes();
      setSavedResumes(resumes);
    } catch (e) {
      console.error("Error loading resumes:", e);
      toast.error("Failed to load your saved resumes");
    } finally {
      setLoadingResumes(false);
    }
  };

  const handleSaveJob = (jobDescription: SavedJobDescription) => {
    setSavedJobData(jobDescription);
    toast.success("Job saved to your library!");
    navigate("/dashboard");
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;
    setProcessingFile(true);
    try {
      let resumeText = "";
      if (file.type === "application/pdf") {
        const fileName = `qual-resume-${Date.now()}.pdf`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("uploaded-documents")
          .upload(fileName, file);
        if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);
        const {
          data: { publicUrl },
        } = supabase.storage.from("uploaded-documents").getPublicUrl(uploadData.path);
        const result = await convertPDFToText(publicUrl);
        resumeText = result.text || "";
      } else if (file.type === "text/plain") {
        resumeText = await file.text();
      } else {
        throw new Error("Please upload a PDF or TXT file.");
      }

      if (!resumeText.trim()) throw new Error("Could not extract text from this file.");

      setUploadedFileName(file.name);
      setUploadedResumeText(resumeText);
      toast.success("Resume processed!");
    } catch (err: any) {
      console.error("File processing error:", err);
      toast.error(err.message || "Failed to process file");
    } finally {
      setProcessingFile(false);
    }
  };

  const getResolvedResumeText = (): string => {
    if (resumeMethod === "saved") {
      const r = savedResumes.find((x) => x.id === selectedResumeId);
      if (!r) return "";
      // Prefer raw content; fall back to a stringified parsed version
      if (r.content && r.content.trim().length > 50) return r.content;
      if (r.parsedResumeData) {
        try {
          return typeof r.parsedResumeData === "string"
            ? r.parsedResumeData
            : JSON.stringify(r.parsedResumeData, null, 2);
        } catch {
          return "";
        }
      }
      return r.content || "";
    }
    if (resumeMethod === "upload") return uploadedResumeText;
    if (resumeMethod === "paste") return pastedResumeText;
    return "";
  };

  const canContinueFromStep2 = (): boolean => {
    const text = getResolvedResumeText();
    return text.trim().length > 50;
  };

  const buildJobDescText = (): string =>
    savedJobData
      ? [
          `Company: ${savedJobData.companyName}`,
          `Job Title: ${savedJobData.jobTitle}`,
          savedJobData.companyMission ? `Mission: ${savedJobData.companyMission}` : "",
          `\nResponsibilities:\n${
            savedJobData.selectedResponsibilities?.map((r) => `• ${r}`).join("\n") || ""
          }`,
          `\nRequirements:\n${
            savedJobData.selectedRequirements?.map((r) => `• ${r}`).join("\n") || ""
          }`,
          `\nSkills:\n${savedJobData.selectedSkills?.map((s) => `• ${s}`).join("\n") || ""}`,
        ]
          .filter(Boolean)
          .join("\n")
      : "";

  const handleAnalyze = async () => {
    if (!savedJobData) {
      toast.error("Please complete step 1 (job description) first.");
      setStep(1);
      return;
    }
    if (!user) {
      toast.error("Please sign in to analyze qualifications.");
      return;
    }
    const resumeText = getResolvedResumeText();
    if (!resumeText || resumeText.trim().length < 50) {
      toast.error("Please select, upload, or paste your resume in step 2.");
      setStep(2);
      return;
    }

    setIsAnalyzing(true);
    try {
      const creditResult = await checkAndDeductCredits("qualifications_match", reportVersion);
      if (!creditResult.success) {
        if (creditResult.error_code === "INSUFFICIENT_CREDITS") {
          setCreditCost(creditResult.cost || 3);
          setShowInsufficientCredits(true);
        } else {
          toast.error(creditResult.message || "Credit check failed");
        }
        setIsAnalyzing(false);
        return;
      }

      const transactionId = creditResult.transaction_id;
      const jobDescText = buildJobDescText();

      const { data, error } = await supabase.functions.invoke("analyze-resume-match", {
        body: {
          action: "qualify_candidate",
          job_description_text: jobDescText,
          resume_text: resumeText,
          report_version: reportVersion,
        },
      });

      if (error || !data?.success) {
        if (transactionId) await refundCredits(transactionId);
        toast.error(data?.error || error?.message || "Analysis failed");
        setIsAnalyzing(false);
        return;
      }

      const result = data.result;

      const savedJob = await findOrCreateSavedJob(
        user.id,
        savedJobData.jobTitle,
        savedJobData.companyName,
        undefined,
        jobDescText,
        "qualification_check"
      );

      const { data: resultRecord, error: saveError } = await supabase
        .from("jobintel_qualification_results" as any)
        .insert({
          user_id: user.id,
          job_title: savedJobData.jobTitle,
          company_name: savedJobData.companyName,
          job_description_text: jobDescText,
          report_version: reportVersion,
          verdict: result.verdict?.classification,
          match_percentage: result.match_percentage?.overall_score || null,
          result_json: result,
          credit_transaction_id: transactionId,
          saved_job_id: savedJob.id,
        } as any)
        .select()
        .single();

      if (!saveError && resultRecord) {
        await linkReportToSavedJob(
          savedJob.id,
          "qualifications_match",
          reportVersion,
          (resultRecord as any).id
        );
      }

      setSavedJobId(savedJob.id);
      setAnalysisResult(result);
      setStep(3);
      toast.success("Qualification analysis complete!");
    } catch (err: any) {
      console.error("Qualification analysis error:", err);
      toast.error(err.message || "Analysis failed");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleStartOver = () => {
    setAnalysisResult(null);
    setSavedJobData(null);
    setSavedJobId(undefined);
    setSelectedResumeId("");
    setPastedResumeText("");
    setUploadedFileName("");
    setUploadedResumeText("");
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-amber-50/50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate("/jobintel-360-reports")}
              className="bg-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center shadow-lg">
              <ClipboardCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Job Qualifications Match</h1>
              <p className="text-muted-foreground">
                Compare your resume to a job posting in 60 seconds
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate("/job-legitimacy-reports")}
            className="bg-white"
          >
            <FileText className="h-4 w-4 mr-2" /> My Reports
          </Button>
        </div>

        {/* Hero Card */}
        {!analysisResult && (
          <Card className="border-2 border-amber-400 bg-amber-50 shadow-lg">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold">
                Determine If You're Qualified in 60 Seconds
              </CardTitle>
              <CardDescription className="text-base leading-relaxed">
                Stop wasting time applying to jobs you're not qualified for. Our AI compares
                <span className="font-semibold text-amber-700"> your resume </span>
                to the job's
                <span className="font-semibold text-amber-700"> responsibilities</span>,
                <span className="font-semibold text-amber-700"> requirements</span>, and
                <span className="font-semibold text-amber-700"> skills</span> — and tells you
                exactly where you fit and where the gaps are.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6 flex-wrap">
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="flex items-center gap-2 text-amber-700 hover:text-amber-800 font-medium transition-colors">
                      <Info className="w-4 h-4" /> Why you need this
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Why You Need Job Qualification Analysis</DialogTitle>
                      <DialogDescription className="space-y-3 pt-4">
                        <p>
                          Most job seekers waste hours applying to positions where they're either
                          overqualified or underqualified.
                        </p>
                        <p>
                          <strong>The problem:</strong> Job postings are often poorly written,
                          mixing "nice-to-haves" with actual requirements.
                        </p>
                        <p>
                          <strong>The solution:</strong> Our AI separates the signal from the
                          noise, comparing your resume to what the role actually demands.
                        </p>
                      </DialogDescription>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="flex items-center gap-2 text-amber-700 hover:text-amber-800 font-medium transition-colors">
                      What you'll discover <ChevronRight className="w-4 h-4" />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>What You'll Discover</DialogTitle>
                      <DialogDescription className="space-y-3 pt-4">
                        <ul className="list-disc list-inside space-y-2">
                          <li>
                            <strong>Core Responsibilities:</strong> What you'll actually be doing
                          </li>
                          <li>
                            <strong>Hard Requirements:</strong> Must-haves vs. nice-to-haves
                          </li>
                          <li>
                            <strong>Skills Breakdown:</strong> Technical and soft skills required
                          </li>
                          <li>
                            <strong>Qualification Verdict:</strong> Qualified, Stretch, or Not
                            Qualified
                          </li>
                          <li>
                            <strong>Gap Analysis:</strong> Exactly what's missing from your resume
                          </li>
                        </ul>
                      </DialogDescription>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border-2 border-amber-600 rounded-lg p-4 text-center shadow-sm">
                  <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-amber-700">60 Sec</div>
                  <div className="text-sm text-muted-foreground">Quick Analysis</div>
                </div>
                <div className="bg-white border-2 border-amber-600 rounded-lg p-4 text-center shadow-sm">
                  <Target className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-amber-700">Resume</div>
                  <div className="text-sm text-muted-foreground">vs. Job</div>
                </div>
                <div className="bg-white border-2 border-amber-600 rounded-lg p-4 text-center shadow-sm">
                  <Zap className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-amber-700">AI</div>
                  <div className="text-sm text-muted-foreground">Smart Extraction</div>
                </div>
                <div className="bg-white border-2 border-amber-600 rounded-lg p-4 text-center shadow-sm">
                  <ClipboardCheck className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-amber-700">Instant</div>
                  <div className="text-sm text-muted-foreground">Qualification Check</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stepper */}
        {!analysisResult && (
          <Card className="border bg-white">
            <CardContent className="py-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <StepBadge step={1} current={step} label="Job Description" />
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                <StepBadge step={2} current={step} label="Your Resume" />
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                <StepBadge step={3} current={step} label="Analysis" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Job Description */}
        {!analysisResult && step === 1 && (
          <Card className="border-2 border-amber-300 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                Step 1: Enter the Job Description
              </CardTitle>
              <CardDescription>
                Paste a job URL, paste the job description text, or fill it in manually
              </CardDescription>
            </CardHeader>
            <CardContent>
              <JobWizardTabs onClose={() => navigate(-1)} onSave={handleSaveJob} />
            </CardContent>
          </Card>
        )}

        {/* Step 2: Resume */}
        {!analysisResult && step === 2 && (
          <Card className="border-2 border-amber-300 bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold">
                    Step 2: Select or Upload Your Resume
                  </CardTitle>
                  <CardDescription>
                    {savedJobData ? (
                      <>
                        Job: <span className="font-medium">{savedJobData.jobTitle}</span> @{" "}
                        <span className="font-medium">{savedJobData.companyName}</span>
                      </>
                    ) : (
                      "Pick how to provide your resume"
                    )}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Edit job
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <RadioGroup
                value={resumeMethod}
                onValueChange={(v: ResumeMethod) => setResumeMethod(v)}
                className="grid grid-cols-1 md:grid-cols-3 gap-3"
              >
                <Label
                  htmlFor="rm-saved"
                  className={`flex items-center gap-2 border-2 rounded-lg p-3 cursor-pointer ${
                    resumeMethod === "saved"
                      ? "border-amber-500 bg-amber-50"
                      : "border-muted hover:border-amber-300"
                  }`}
                >
                  <RadioGroupItem value="saved" id="rm-saved" />
                  <FileText className="w-4 h-4" /> Saved resume
                </Label>
                <Label
                  htmlFor="rm-upload"
                  className={`flex items-center gap-2 border-2 rounded-lg p-3 cursor-pointer ${
                    resumeMethod === "upload"
                      ? "border-amber-500 bg-amber-50"
                      : "border-muted hover:border-amber-300"
                  }`}
                >
                  <RadioGroupItem value="upload" id="rm-upload" />
                  <Upload className="w-4 h-4" /> Upload file
                </Label>
                <Label
                  htmlFor="rm-paste"
                  className={`flex items-center gap-2 border-2 rounded-lg p-3 cursor-pointer ${
                    resumeMethod === "paste"
                      ? "border-amber-500 bg-amber-50"
                      : "border-muted hover:border-amber-300"
                  }`}
                >
                  <RadioGroupItem value="paste" id="rm-paste" />
                  <ClipboardCheck className="w-4 h-4" /> Paste text
                </Label>
              </RadioGroup>

              {resumeMethod === "saved" && (
                <div className="space-y-2">
                  <Label className="text-sm">Choose from your library</Label>
                  {loadingResumes ? (
                    <div className="flex items-center gap-2 text-muted-foreground py-3">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading your resumes...
                    </div>
                  ) : savedResumes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No saved resumes yet — switch to Upload or Paste, or save one in your Resume
                      Library first.
                    </p>
                  ) : (
                    <Select value={selectedResumeId} onValueChange={setSelectedResumeId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a resume" />
                      </SelectTrigger>
                      <SelectContent>
                        {savedResumes.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.title || r.fileName || `Resume ${r.id.slice(0, 8)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {resumeMethod === "upload" && (
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-amber-300 rounded-lg p-8 text-center bg-amber-50/40">
                    <Upload className="h-10 w-10 mx-auto text-amber-600 mb-3" />
                    <Label htmlFor="qual-resume-upload" className="cursor-pointer">
                      <span className="text-amber-700 font-medium hover:underline">
                        Click to upload
                      </span>{" "}
                      or drag a PDF / TXT file
                    </Label>
                    <Input
                      id="qual-resume-upload"
                      type="file"
                      accept=".pdf,.txt"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFileUpload(f);
                      }}
                    />
                  </div>
                  {processingFile && (
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Processing your resume...
                    </div>
                  )}
                  {uploadedFileName && !processingFile && (
                    <div className="flex items-center gap-2 text-green-700 text-sm">
                      <CheckCircle2 className="w-4 h-4" /> Loaded: {uploadedFileName}
                    </div>
                  )}
                </div>
              )}

              {resumeMethod === "paste" && (
                <div className="space-y-2">
                  <Label htmlFor="qual-paste">Paste resume text</Label>
                  <Textarea
                    id="qual-paste"
                    rows={10}
                    placeholder="Paste your resume content here..."
                    value={pastedResumeText}
                    onChange={(e) => setPastedResumeText(e.target.value)}
                  />
                </div>
              )}

              <div className="border-t pt-4 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold mb-2">Choose Report Depth</h4>
                  <ReportVersionSelector
                    value={reportVersion}
                    onChange={setReportVersion}
                  />
                </div>

                <div className="flex justify-between gap-2">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button
                    onClick={handleAnalyze}
                    disabled={isAnalyzing || !canContinueFromStep2()}
                    className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white px-6 py-5 text-base font-semibold"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analyzing...
                      </>
                    ) : (
                      <>
                        <ClipboardCheck className="w-5 h-5 mr-2" /> Analyze My Qualifications
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3 / Results */}
        {analysisResult && (
          <>
            <div className="flex justify-end">
              <Button variant="outline" onClick={handleStartOver} className="bg-white">
                Start Over
              </Button>
            </div>
            <QualificationMatchResults
              result={analysisResult}
              reportVersion={reportVersion}
              savedJobId={savedJobId}
              jobTitle={savedJobData?.jobTitle}
              companyName={savedJobData?.companyName}
              jobDescriptionText={buildJobDescText()}
            />
          </>
        )}

        <InsufficientCreditsModal
          open={showInsufficientCredits}
          onClose={() => setShowInsufficientCredits(false)}
          currentBalance={access?.credits_remaining || 0}
          requiredCost={creditCost}
        />
      </div>
    </div>
  );
};

export default JobQualificationCheck;
